import React from "react";
import moment from "moment";
import RequestEngine from "../../core/RequestEngine";
import strings from "../../core/translate";
import { apiErrorMessage } from "../../shared/api-error";
import {
  FormSection,
  FormGrid,
  TextField,
  SelectField,
  TimeField,
  FormAlert,
  FormActions,
} from "../../components/Form/FormKit";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// the api returns "starts at: HH:mm, ends at: HH:mm" but accepts "HH:mm-HH:mm"
const parseRange = (value) => {
  if (!value) return [null, null];
  const labelled = `${value}`.match(
    /starts at:\s*(\d{1,2}:\d{2}),\s*ends at:\s*(\d{1,2}:\d{2})/
  );
  const plain = `${value}`.match(/^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/);
  const matches = labelled || plain;
  if (!matches) return [null, null];
  return [moment(matches[1], "HH:mm"), moment(matches[2], "HH:mm")];
};

const formatRange = (start, end) =>
  `${moment(start).format("HH:mm")}-${moment(end).format("HH:mm")}`;

export default class AppointmentSettingsForm extends React.Component {
  constructor(props) {
    super(props);
    const item = props.item || {};
    const [startWorkingHours, endWorkingHours] = parseRange(item.workingHours);
    const [startBreakTime, endBreakTime] = parseRange(item.breakTime);
    // stored as a two digit number: first day index + last day index
    const days = `${item.workingDays === undefined ? "" : item.workingDays}`
      .trim()
      .padStart(2, "0");

    this.state = {
      id: item.id || "",
      version: item.version,
      startWorkingHours,
      endWorkingHours,
      startBreakTime,
      endBreakTime,
      firstWorkingDay: DAYS[Number(days.charAt(0))] || "Monday",
      lastWorkingDay: DAYS[Number(days.charAt(1))] || "Friday",
      appointmentsFrequency:
        item.appointmentsFrequency === undefined ? "" : item.appointmentsFrequency,
      appointmentsPerSlot:
        item.appointmentsPerSlot === undefined ? "" : item.appointmentsPerSlot,
      slotBookingThreshold:
        item.slotBookingThreshold === undefined ? 0 : item.slotBookingThreshold,
      loading: false,
      error: "",
      errors: {},
    };
    this.engine = new RequestEngine();
  }

  validate() {
    const {
      startWorkingHours,
      endWorkingHours,
      startBreakTime,
      endBreakTime,
      appointmentsFrequency,
      appointmentsPerSlot,
    } = this.state;
    const errors = {};

    if (!startWorkingHours || !endWorkingHours) {
      errors.workingHours = strings.hours_required_hint;
    }
    if (!startBreakTime || !endBreakTime) {
      errors.breakTime = strings.hours_required_hint;
    }
    if (`${appointmentsFrequency}`.trim() === "") {
      errors.appointmentsFrequency = strings.required_hint;
    }
    if (`${appointmentsPerSlot}`.trim() === "") {
      errors.appointmentsPerSlot = strings.required_hint;
    }
    return errors;
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    const {
      id,
      version,
      startWorkingHours,
      endWorkingHours,
      startBreakTime,
      endBreakTime,
      firstWorkingDay,
      lastWorkingDay,
      appointmentsFrequency,
      appointmentsPerSlot,
      slotBookingThreshold,
    } = this.state;

    this.setState({ loading: true, error: "", errors: {} });

    const payload = {
      workingHours: formatRange(startWorkingHours, endWorkingHours),
      breakTime: formatRange(startBreakTime, endBreakTime),
      workingDays: Number(
        `${DAYS.indexOf(firstWorkingDay)}${DAYS.indexOf(lastWorkingDay)}`
      ),
      appointmentsFrequency: Number(appointmentsFrequency),
      appointmentsPerSlot: Number(appointmentsPerSlot),
      slotBookingThreshold: Number(slotBookingThreshold),
    };

    const pathname = `merchants-branches/${this.props.branchId}/appointments-settings`;

    try {
      const response = id
        ? await this.engine.updateItem(pathname, id, { ...payload, version })
        : await this.engine.saveItem(pathname, payload);

      if (response && (response.status === 200 || response.status === 201)) {
        this.setState({ loading: false });
        this.props.onSaved(response.data);
        return;
      }
    } catch (err) {
      this.setState({ error: apiErrorMessage(err) });
    }
    this.setState({ loading: false });
  };

  render() {
    const {
      startWorkingHours,
      endWorkingHours,
      startBreakTime,
      endBreakTime,
      firstWorkingDay,
      lastWorkingDay,
      appointmentsFrequency,
      appointmentsPerSlot,
      loading,
      error,
      errors,
    } = this.state;

    const dayOptions = DAYS.map((day) => ({
      value: day,
      label: strings[day.toLowerCase()] || day,
    }));

    return (
      <form className="mform" onSubmit={this.handleSubmit}>
        <FormSection title={strings.workinghours} icon="nc-icon nc-watch-time">
          <FormGrid>
            <TimeField
              label={strings.starttime}
              required
              value={startWorkingHours}
              onChange={(value) => this.setState({ startWorkingHours: value })}
              error={errors.workingHours}
            />
            <TimeField
              label={strings.endtime}
              required
              value={endWorkingHours}
              onChange={(value) => this.setState({ endWorkingHours: value })}
            />
          </FormGrid>
          <FormGrid>
            <SelectField
              label={strings.firstday}
              value={firstWorkingDay}
              options={dayOptions}
              onChange={(value) => this.setState({ firstWorkingDay: value })}
            />
            <SelectField
              label={strings.lastday}
              value={lastWorkingDay}
              options={dayOptions}
              onChange={(value) => this.setState({ lastWorkingDay: value })}
            />
          </FormGrid>
        </FormSection>

        <FormSection title={strings.breaktime} icon="nc-icon nc-button-pause">
          <FormGrid>
            <TimeField
              label={strings.starttime}
              required
              value={startBreakTime}
              onChange={(value) => this.setState({ startBreakTime: value })}
              error={errors.breakTime}
            />
            <TimeField
              label={strings.endtime}
              required
              value={endBreakTime}
              onChange={(value) => this.setState({ endBreakTime: value })}
            />
          </FormGrid>
        </FormSection>

        <FormSection
          title={strings.appointments}
          icon="nc-icon nc-settings-gear-65"
        >
          <FormGrid>
            <TextField
              label={strings.appointmentsfrequency}
              type="number"
              min="0"
              required
              value={appointmentsFrequency}
              onChange={(value) =>
                this.setState({ appointmentsFrequency: value })
              }
              error={errors.appointmentsFrequency}
              help={strings.frequency_hint}
            />
            <TextField
              label={strings.appointmentsperslot}
              type="number"
              min="0"
              required
              value={appointmentsPerSlot}
              onChange={(value) => this.setState({ appointmentsPerSlot: value })}
              error={errors.appointmentsPerSlot}
            />
          </FormGrid>
        </FormSection>

        <FormAlert>{error}</FormAlert>
        <FormActions onCancel={this.props.onCancel} loading={loading} />
      </form>
    );
  }
}
